import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { countries } from "@/data/countries";
import "flag-icons/css/flag-icons.min.css";

interface CountryCodeSelectProps {
  value: string;
  onChange: (code: string) => void;
  className?: string;
}

export function CountryCodeSelect({ value, onChange, className = "w-[110px]" }: CountryCodeSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize search term when value changes externally
  useEffect(() => {
    if (value && !showDropdown) {
      const country = countries.find((c) => c.code === value);
      if (country) {
        setSearchTerm(`${country.name} (${country.code})`);
      } else {
        setSearchTerm(value);
      }
    }
  }, [value, showDropdown]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        // Reset search term if not selecting anything
        if (value) {
          const country = countries.find((c) => c.code === value);
          setSearchTerm(country ? `${country.name} (${country.code})` : value);
        } else {
          setSearchTerm("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleSelectCountry = (country: any) => {
    setSearchTerm(`${country.name} (${country.code})`);
    onChange(country.code);
    setShowDropdown(false);
  };

  const selectedFormatted = value 
    ? (() => {
        const c = countries.find((x) => x.code === value);
        return c ? `${c.name} (${c.code})` : value;
      })()
    : "";

  const isPristine = searchTerm === selectedFormatted;

  const filteredCountries = isPristine
    ? countries
    : countries.filter(
        (c) =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.code.includes(searchTerm)
      );

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setShowDropdown(true);
    e.target.select();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <Input
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder="Código"
        className="w-full text-ellipsis pr-2"
      />
      {showDropdown && (
        <div className="absolute z-[100] bg-white border mt-1 max-h-60 overflow-y-auto w-64 shadow-lg rounded left-0">
          {filteredCountries.map((c) => (
            <div
              key={c.code}
              className="cursor-pointer px-3 py-2 hover:bg-slate-100 flex items-center gap-2"
              onClick={() => handleSelectCountry(c)}
            >
              <span className={`fi fi-${c.flagCode} w-6 h-4 flex-shrink-0 shadow-sm border border-slate-200`}></span>
              <span className="truncate">
                {c.name} <span className="text-slate-500 ml-1">({c.code})</span>
              </span>
            </div>
          ))}
          {filteredCountries.length === 0 && (
            <div className="px-3 py-2 text-slate-400 text-sm">
              No se encontró ningún país
            </div>
          )}
        </div>
      )}
    </div>
  );
}
